import type {
  ProfilePropertiesRequest,
  ProfilePropertiesWidgetKey,
  PropertiesBucket,
  PropertiesQueryParams,
  SocialPropertiesQueryParams,
} from '../types/riskProfileProperties.types'
import type { RiskProfilePropertiesElementConfig } from '../hooks/useRiskProfilePropertiesElementConfig'
import { resolveRegionFilters } from './riskProfile.utils'

interface BuildPropertiesRequestOptions {
  queryParams: PropertiesQueryParams | SocialPropertiesQueryParams
  widget: ProfilePropertiesWidgetKey
  hazardGroupBuckets: PropertiesBucket[]
  elementConfig: RiskProfilePropertiesElementConfig
}

function filterHazardBuckets(buckets: PropertiesBucket[], selectedGroups: string[]) {
  if (selectedGroups.length === 0) {
    return buckets
  }
  return buckets.filter((bucket) => selectedGroups.includes(Object.keys(bucket)[0]))
}

function flattenBucketIds(buckets: PropertiesBucket[]) {
  return buckets.flatMap((bucket) => Object.values(bucket)[0])
}

function isSocialQueryParams(
  queryParams: PropertiesQueryParams | SocialPropertiesQueryParams,
): queryParams is SocialPropertiesQueryParams {
  return 'demographicView' in queryParams
}

export function buildRiskProfileSocialEconomicRequest({
  queryParams,
  widget,
  hazardGroupBuckets,
  elementConfig,
}: BuildPropertiesRequestOptions): ProfilePropertiesRequest {
  const regionFilters = resolveRegionFilters(queryParams.areas, queryParams.areaGroup)

  const filteredHazardBuckets = filterHazardBuckets(
    hazardGroupBuckets,
    queryParams.selectedHazardGroups,
  )

  const filteredHazardIds =
    queryParams.selectedHazardGroups.length === 0
      ? queryParams.hazards
      : flattenBucketIds(filteredHazardBuckets)

  const baseRequest = {
    metric: queryParams.metric as ProfilePropertiesRequest['metric'],
    element_ids: elementConfig.element_ids,
    property_lookup_key: elementConfig.property_lookup_key,
    ...regionFilters,
  }

  switch (widget) {
    case 'residentsByDemographic': {
      if (!isSocialQueryParams(queryParams)) {
        throw new Error('residentsByDemographic requires demographicView on queryParams')
      }
      return {
        ...baseRequest,
        group_by: queryParams.demographicView,
        hazards: filteredHazardIds,
      }
    }

    case 'residentsByHazard':
    case 'disruptionByHazard':
      return {
        ...baseRequest,
        group_by: { hazard_group: filteredHazardBuckets },
      }

    // Economic only — metric IS the group_by here, confirmed via
    // { metric: 'employees', group_by: 'employees', ... } live example.
    case 'disruptionBySector':
      return {
        ...baseRequest,
        group_by: queryParams.metric as ProfilePropertiesRequest['group_by'],
        hazards: filteredHazardIds,
      }
    case 'residentsByArea':
    case 'disruptionByArea':
      return {
        ...baseRequest,
        group_by: queryParams.areas.length > 0 ? 'region' : 'region_group',
        hazards: filteredHazardIds,
      }
  }
}
